import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import Colors from '@/utils/colors'
import GraphCard from './GraphCard.vue'
import D3StackedBarHorizontalvue from './D3StackedBarHorizontal.vue'
import {
  PlotSettings,
  ToplistQuery,
  ToplistResponse,
  ToplistValue
} from '@/api-client/models/utilization/Contracts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { CardState } from '../../types'
import { FeatureType, useFilters } from '@/composables/useFilters'
import fetches from '@/fetches'

type Props = {
  state: CardState
  data?: ToplistResponse
  hasError?: Error
  settings: PlotSettings
  onlyBookable: boolean
}
type DatasetValue = {
  room: string
  roomAlias: string
  used: number
  booked: number
  usedAndBooked: number
  notUsedNotBooked: number
}

type DatasetColors = {
  used: string
  usedAndBooked: string
  booked: string
  notUsedNotBooked: string
}

type DatasetNames = {
  used: string
  usedAndBooked: string
  booked: string
  notUsedNotBooked: string
}

type DatasetHatching = {
  used: boolean
  usedAndBooked: boolean
  booked: boolean
  notUsedNotBooked: boolean
}

type Dataset = {
  values: DatasetValue[]
  columns: string[]
  colors: DatasetColors
  names: DatasetNames
  hatched: DatasetHatching
}

export default /*@__PURE__*/_defineComponent({
  __name: 'GraphToplistCard',
  props: {
    state: {},
    data: {},
    hasError: {},
    settings: {},
    onlyBookable: { type: Boolean }
  },
  setup(__props: any) {

const { t, locale } = useI18n()

const queryRef = computed(
  () =>
    fetches['fetch.utilization.toplist'](useFilters(FeatureType.Utilization).submitted).query as
      | ToplistQuery
      | undefined
)

const stateOverride = computed<CardState>(() => {
  const filter = queryRef.value?.filter
  if (
    filter?.entityType &&
    ['CONSTRUCTION', 'LEVEL'].includes(filter.entityType) &&
    filter.entityIds.length === 1
  ) {
    if (props.state === 'ACTIVE' && props.data?.values.length === 0) {
      return 'EMPTY'
    }
    return props.state
  }
  return 'DISABLED'
})

const createDefaultDataset = (onlyBookable: boolean): Dataset => ({
  values: [],
  columns: [],
  colors: {
    used: Colors.graph.blue,
    usedAndBooked: Colors.graph.blue,
    booked: Colors.graph.gray,
    notUsedNotBooked: Colors.graph.gray
  },
  names: {
    used: t('general__text__used') + (onlyBookable ? ', ' + t('general__text__not-booked') : ''),
    usedAndBooked: t('general__text__used') + ', ' + t('general__text__booked'),
    booked: t('general__text__booked') + ', ' + t('general__text__not-used'),
    notUsedNotBooked:
      (onlyBookable ? t('general__text__not-booked') + ', ' : '') + t('general__text__not-used')
  },
  hatched: {
    used: false,
    usedAndBooked: true,
    booked: true,
    notUsedNotBooked: false
  }
})

const dataset = computed<Dataset>(() => {
  if (!props.data) return createDefaultDataset(props.onlyBookable)

  const ds = createDefaultDataset(props.onlyBookable)

  const { numberOfHours, values } = props.data
  const mappedValues = values.map((value) => valueMapper(value, numberOfHours))
  mappedValues.sort(
    (a, b) => b.used + b.usedAndBooked + b.booked - (a.used + a.usedAndBooked + a.booked)
  )
  ds.values = mappedValues
  ds.columns = ['used', 'usedAndBooked', 'booked', 'notUsedNotBooked']
  return ds
})

const props = __props

const filterMessage = computed(() => {
  return stateOverride.value === 'DISABLED'
    ? t('general__text__filter-by-floor')
    : t('general__data__data-missing')
})

function valueMapper(toplistValue: ToplistValue, numberOfHours: number): DatasetValue {
  const used = (toplistValue.used / numberOfHours) * 100
  const booked = (toplistValue.booked / numberOfHours) * 100
  const usedAndBooked = (toplistValue.bookedAndUsed / numberOfHours) * 100

  return {
    room: toplistValue.name ?? '',
    roomAlias: toplistValue.alias ?? '',
    used: used,
    booked: booked,
    usedAndBooked: usedAndBooked,
    notUsedNotBooked: 100 - used - booked - usedAndBooked
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(GraphCard, {
    title: _ctx.settings.title[_unref(locale)],
    state: stateOverride.value,
    hasError: stateOverride.value === 'ERROR' ? _ctx.hasError : undefined,
    additionalInfo: _ctx.settings.info?.[_unref(locale)],
    filterMessage: filterMessage.value
  }, {
    default: _withCtx(() => [
      _createVNode(D3StackedBarHorizontalvue, {
        id: 'BookingUnit',
        dataset: dataset.value
      }, null, 8, ["dataset"])
    ]),
    _: 1
  }, 8, ["title", "state", "hasError", "additionalInfo", "filterMessage"]))
}
}

})